
import { Injectable, signal, computed } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { IAnswer } from 'src/app/interfaces/ianswer';
import { ICallPractice } from 'src/app/interfaces/icall-practice';
import { IPreloadedImageResult } from 'src/app/interfaces/ipreloaded-image-result';
import { IWords } from 'src/app/interfaces/iwords';
import { IPersonalizedLaunchPayload } from 'src/app/interfaces/personalized-practice/ipersonalized-launch';
import { IPathwayQuizResult } from 'src/app/interfaces/ipathway-quiz-result';
import { CallModalType } from 'src/app/types';
import { TranscriptCorrections } from 'src/app/utils/transcript-corrector';

@Injectable({
  providedIn: 'root',
})
export class CallStoreService {
  // Signals for state management

  // Global logging properties - used across all call logs
  private userUuid = signal<string>('');
  private moduleAssignmentUuid = signal<string>('');
  private moduleAssignmentType = signal<string>('');
  private callUuid = signal<string>('');
  private callType = signal<string>('');
  private practiceCallUuid = signal<string>('');
  private isPersonalizedCall = signal<boolean>(false);

  // Call
  private currentView = signal<string | null>(null);
  private call = signal<ICallPractice | null>(null);
  private isPaused = signal<boolean>(false);
  private advanceType = signal<string>('MANUAL');
  private displayPostCall = signal<boolean>(false);
  private replaceWords = signal<IWords[]>([]);
  private pronounceWords = signal<IWords[]>([]);
  private callWords = signal<string[]>([]);
  private sttWords = signal<string[]>([]);
  private isCallComplete = signal<boolean>(false);
  // Tracks whether the guided call's final (goodbye) message has actually finished
  // playing — set only on the real Tavus stopped_speaking event for the final clip,
  // never inferred from isAvatarSpeaking alone. See SR-16661.
  private goodbyeConfirmed = signal<boolean>(false);
  private isGuidedCallReadyToEnd = computed(
    () => this.isCallComplete() && this.goodbyeConfirmed(),
  );
  private quizResultReady = signal<boolean>(false);
  private showQuizResultView = signal<boolean>(false);
  private hints = signal<string>('');
  private isResumeFromModal = signal<boolean>(false);
  private resumeFromModalType = signal<CallModalType | null>(null);
  private blockingModalOpenCount = signal<number>(0);
  private displayAlert = signal<boolean>(false);
  private isFullScreen = signal<boolean>(false);
  private isVisualAidOpen = signal<boolean>(false);
  private visualAidImageUrl = signal<string>('');
  private visualAidImages = signal<IPreloadedImageResult[]>([]);
  private maxAttemptsAllowed = signal<number | null>(null);

  // Transcript
  private lastMessageTimeStamp = signal<Date>(new Date());
  private recognizedTranscripts = signal<string[]>([]);
  private speechTranscript = signal<string>('');
  private speechTranscriptPartial = signal<string>('');
  private partialTranscriptExists = signal<boolean>(false);
  private transcriptChanges = new TranscriptCorrections();
  private transcriptions = signal<string[]>([]);

  // Answer
  private isSendingAnswer = signal<boolean>(false);
  private isWaitingTimeout = signal<boolean>(false);
  private answerData = signal<IAnswer | null>(null);
  private wasSendingAnswer = signal<boolean>(false);

  // Avatar
  private isAvatarSpeaking = signal<boolean>(false);
  private isAvatarThinking = signal<boolean>(false);

  // Buttons
  private isContinueButtonBlocked = signal<boolean>(false);
  private isContinueButtonHidden = signal<boolean>(false);
  private isMicOn = signal<boolean>(true);
  private userMicPreference = signal<boolean>(true);
  private disableEndCallButton = signal<boolean>(false);

  // User
  private isAudioDeviceSelected = signal<boolean>(false);

  // Error handling
  private isLoadingNoResponse = signal<boolean>(false);
  private openNoResponseModal = signal<boolean>(false);
  private isErrorOnAudioValidation = signal<boolean>(false);

  // User Spoke Signal
  private userSpoke = signal<number>(Date.now());

  // Devices
  private isIpad = signal<boolean>(false);

  // Processing state
  private isProcessingAnswer = signal<boolean>(false);
  private skipConnectTranscriber = signal<boolean>(false);
  private isAssemblyInitialized = signal<boolean>(false);
  private isReconnectingAudio = signal<boolean>(false);
  private personalizedLaunchPayload = signal<IPersonalizedLaunchPayload | null>(null);
  private isAudioCallMode = signal<boolean>(false);

  // Local Storage flags
  private language = signal<string>('');

  /* Signal getters and setters */

  // Global logging properties
  getUserUuid() {
    return this.userUuid();
  }
  setUserUuid(uuid: string) {
    this.userUuid.set(uuid);
  }

  getModuleAssignmentUuid() {
    return this.moduleAssignmentUuid();
  }
  setModuleAssignmentUuid(uuid: string) {
    this.moduleAssignmentUuid.set(uuid);
  }

  getModuleAssignmentType() {
    return this.moduleAssignmentType();
  }
  setModuleAssignmentType(type: string) {
    this.moduleAssignmentType.set(type);
  }

  getCallUuid() {
    return this.callUuid();
  }
  setCallUuid(uuid: string) {
    this.callUuid.set(uuid);
  }

  getCallType() {
    return this.callType();
  }
  setCallType(type: string) {
    this.callType.set(type);
  }

  getPracticeCallUuid() {
    return this.practiceCallUuid();
  }
  setPracticeCallUuid(uuid: string) {
    this.practiceCallUuid.set(uuid);
  }

  getIsPersonalizedCall() {
    return this.isPersonalizedCall();
  }
  setIsPersonalizedCall(isPersonalized: boolean) {
    this.isPersonalizedCall.set(isPersonalized);
  }

  /**
   * Get all global logging properties in a single object
   * This is the standard properties object that should be included in all call logs
   */
  getGlobalLogProperties() {
    return {
      userUuid: this.userUuid(),
      moduleAssignmentUuid: this.moduleAssignmentUuid(),
      moduleAssignmentType: this.moduleAssignmentType(),
      callUuid: this.callUuid(),
      callType: this.callType(),
      practiceCallUuid: this.practiceCallUuid(),
    };
  }

  // Call
  getCurrentView() {
    return this.currentView();
  }
  getCurrentViewSignal() {
    return this.currentView.asReadonly();
  }
  setCurrentView(view: string | null) {
    this.currentView.set(view);
  }

  getCall() {
    return this.call();
  }
  setCall(call: ICallPractice | null) {
    this.call.set(call);
  }

  getIsPaused() {
    return this.isPaused();
  }
  setIsPaused(isPaused: boolean) {
    this.isPaused.set(isPaused);
  }

  getAdvanceType() {
    return this.advanceType();
  }
  setAdvanceType(advanceType: string) {
    this.advanceType.set(advanceType);
  }

  getDisplayPostCall() {
    return this.displayPostCall();
  }
  setDisplayPostCall(display: boolean) {
    this.displayPostCall.set(display);
  }

  getReplaceWords() {
    return this.replaceWords();
  }
  setReplaceWords(replaceWords: IWords[]) {
    this.replaceWords.set(replaceWords);
  }

  getPronounceWords() {
    return this.pronounceWords();
  }
  setPronounceWords(pronounceWords: IWords[]) {
    this.pronounceWords.set(pronounceWords);
  }

  getCallWords() {
    return this.callWords();
  }
  setCallWords(callWords: string[]) {
    this.callWords.set(callWords);
  }

  getSttWords() {
    return this.sttWords();
  }
  setSttWords(sttWords: string[]) {
    this.sttWords.set(sttWords);
  }

  getIsCallComplete() {
    return this.isCallComplete();
  }
  setIsCallComplete(isComplete: boolean) {
    this.isCallComplete.set(isComplete);
  }

  getGoodbyeConfirmed() {
    return this.goodbyeConfirmed();
  }
  setGoodbyeConfirmed(isConfirmed: boolean) {
    this.goodbyeConfirmed.set(isConfirmed);
  }

  getIsGuidedCallReadyToEnd() {
    return this.isGuidedCallReadyToEnd();
  }

  getHints() {
    return this.hints();
  }
  setHints(hints: string) {
    this.hints.set(hints);
  }

  getIsResumeFromModal() {
    return this.isResumeFromModal();
  }
  setIsResumeFromModal(isOpen: boolean) {
    this.isResumeFromModal.set(isOpen);
  }

  getIsAnyModalOpen(): boolean {
    return this.blockingModalOpenCount() > 0;
  }
  registerModalOpen(): void {
    this.blockingModalOpenCount.update(count => count + 1);
  }
  registerModalClosed(): void {
    this.blockingModalOpenCount.update(count => Math.max(0, count - 1));
  }

  getResumeFromModalType() {
    return this.resumeFromModalType();
  }
  setResumeFromModalType(type: CallModalType | null) {
    this.resumeFromModalType.set(type);
  }

  getDisplayAlert() {
    return this.displayAlert();
  }

  setDisplayAlert(value: boolean) {
    this.displayAlert.set(value);
  }

  getIsFullScreen() {
    return this.isFullScreen();
  }

  setIsFullScreen(isFullScreen: boolean) {
    this.isFullScreen.set(isFullScreen);
  }

  getIsVisualAidOpen() {
    return this.isVisualAidOpen();
  }
  setIsVisualAidOpen(isOpen: boolean) {
    this.isVisualAidOpen.set(isOpen);
  }

  getVisualAidImageUrl() {
    return this.visualAidImageUrl();
  }
  setVisualAidImageUrl(url: string) {
    this.visualAidImageUrl.set(url);
  }

  getVisualAidImages() {
    return this.visualAidImages();
  }
  setVisualAidImages(images:  IPreloadedImageResult[]) {
    this.visualAidImages.set(images);
  }

  getUserSpoke() {
    return this.userSpoke();
  }

  setUserSpoke(value: number) {
    this.userSpoke.set(value);
  }

  getMaxAttemptsAllowed(): number | null {
    return this.maxAttemptsAllowed();
  }

  setMaxAttemptsAllowed(value: number | null): void {
    this.maxAttemptsAllowed.set(value);
  }

  // Transcript
  getLastMessageTimeStamp() {
    return this.lastMessageTimeStamp();
  }
  setLastMessageTimeStamp(timestamp: Date) {
    this.lastMessageTimeStamp.set(timestamp);
  }

  getRecognizedTranscripts() {
    return this.recognizedTranscripts();
  }
  setRecognizedTranscripts(transcripts: string[]) {
    this.recognizedTranscripts.set(transcripts);
  }

  getSpeechTranscript() {
    return this.speechTranscript();
  }
  setSpeechTranscript(transcript: string) {
    this.speechTranscript.set(transcript);
  }

  getSpeechTranscriptPartial() {
    return this.speechTranscriptPartial();
  }
  setSpeechTranscriptPartial(transcript: string) {
    this.speechTranscriptPartial.set(transcript);
  }

  getPartialTranscriptExists() {
    return this.partialTranscriptExists();
  }
  setPartialTranscriptExists(exists: boolean) {
    this.partialTranscriptExists.set(exists);
  }

  getTranscriptChanges() {
    return this.transcriptChanges;
  }
  setTranscriptChanges() {
    this.transcriptChanges = new TranscriptCorrections();
  }
  addTranscriptChangesLogs(changes: any): void {
    this.transcriptChanges.addTranscriptChangesLogs(changes);
  }

  getTranscriptions() {
    return this.transcriptions();
  }
  setTranscriptions(transcriptions: string[]) {
    this.transcriptions.set(transcriptions);
  }

  // Answer
  getIsSendingAnswer() {
    return this.isSendingAnswer();
  }
  setIsSendingAnswer(isSending: boolean) {
    this.isSendingAnswer.set(isSending);
  }

  getIsWaitingTimeout() {
    return this.isWaitingTimeout();
  }
  setIsWaitingTimeout(isWaiting: boolean) {
    this.isWaitingTimeout.set(isWaiting);
  }

  getAnswerData() {
    return this.answerData();
  }
  setAnswerData(answer: IAnswer | null) {
    this.answerData.set(answer);
  }

  getWasSendingAnswer() {
    return this.wasSendingAnswer();
  }
  setWasSendingAnswer(wasSending: boolean) {
    this.wasSendingAnswer.set(wasSending);
  }

  // Avatar
  getIsAvatarSpeaking() {
    return this.isAvatarSpeaking();
  }
  setIsAvatarSpeaking(isSpeaking: boolean) {
    this.isAvatarSpeaking.set(isSpeaking);
  }

  getIsAvatarThinking() {
    return this.isAvatarThinking();
  }
  setIsAvatarThinking(isThinking: boolean) {
    this.isAvatarThinking.set(isThinking);
  }

  // Buttons
  getIsContinueButtonBlocked() {
    return this.isContinueButtonBlocked();
  }
  setIsContinueButtonBlocked(isBlocked: boolean) {
    this.isContinueButtonBlocked.set(isBlocked);
  }

  getIsContinueButtonHidden() {
    return this.isContinueButtonHidden();
  }
  setIsContinueButtonHidden(isHidden: boolean) {
    this.isContinueButtonHidden.set(isHidden);
  }

  getIsMicOn() {
    return this.isMicOn();
  }
  setIsMicOn(isOn: boolean) {
    this.isMicOn.set(isOn);
  }

  getUserMicPreference() {
    return this.userMicPreference();
  }
  setUserMicPreference(preference: boolean) {
    this.userMicPreference.set(preference);
  }

  getDisableEndCallButton() {
    return this.disableEndCallButton();
  }
  setDisableEndCallButton(isDisabled: boolean) {
    this.disableEndCallButton.set(isDisabled);
  }

  // User
  getIsAudioDeviceSelected() {
    return this.isAudioDeviceSelected();
  }
  setIsAudioDeviceSelected(isSelected: boolean) {
    this.isAudioDeviceSelected.set(isSelected);
  }

  // Error handling
  getIsLoadingNoResponse() {
    return this.isLoadingNoResponse();
  }
  setIsLoadingNoResponse(isLoading: boolean) {
    this.isLoadingNoResponse.set(isLoading);
  }

  getOpenNoResponseModal() {
    return this.openNoResponseModal();
  }
  setOpenNoResponseModal(isOpen: boolean) {
    this.openNoResponseModal.set(isOpen);
  }

  getIsErrorOnAudioValidation() {
    return this.isErrorOnAudioValidation();
  }
  setIsErrorOnAudioValidation(isError: boolean) {
    this.isErrorOnAudioValidation.set(isError);
  }

  // Devices
  getIsIpad() {
    return this.isIpad();
  }
  setIsIpad(isIpad: boolean) {
    this.isIpad.set(isIpad);
  }

  // Processing state
  getIsProcessingAnswer() {
    return this.isProcessingAnswer();
  }
  setIsProcessingAnswer(isProcessing: boolean) {
    this.isProcessingAnswer.set(isProcessing);
  }

  getSkipConnectTranscriber() {
    return this.skipConnectTranscriber();
  }
  setSkipConnectTranscriber(skip: boolean) {
    this.skipConnectTranscriber.set(skip);
  }

  getIsAssemblyInitialized() {
    return this.isAssemblyInitialized();
  }
  setIsAssemblyInitialized(isInitialized: boolean) {
    this.isAssemblyInitialized.set(isInitialized);
  }

  getIsReconnectingAudio() {
    return this.isReconnectingAudio();
  }
  setIsReconnectingAudio(value: boolean) {
    this.isReconnectingAudio.set(value);
  }

  getIsAudioCallMode() {
    return this.isAudioCallMode();
  }
  setIsAudioCallMode(isAudioCall: boolean) {
    this.isAudioCallMode.set(isAudioCall);
  }

  getPersonalizedLaunchPayload() {
    return this.personalizedLaunchPayload();
  }
  setPersonalizedLaunchPayload(payload: IPersonalizedLaunchPayload | null) {
    this.personalizedLaunchPayload.set(payload);
  }

  // Local Storage flags
  getLanguage() {
    return this.language();
  }
  setLanguage(language: string) {
    this.language.set(language);
  }

  getQuizResultReady() {
    return this.quizResultReady();
  }
  setQuizResultReady(ready: boolean) {
    this.quizResultReady.set(ready);
  }

  private quizResultData = signal<IPathwayQuizResult | null>(null);

  getQuizResultData(): IPathwayQuizResult | null {
    return this.quizResultData();
  }
  setQuizResultData(data: IPathwayQuizResult): void {
    this.quizResultData.set(data);
  }

  private readonly quizResultDataReady$ = new Subject<void>();
  private readonly quizResultDataError$ = new Subject<void>();
  private readonly quizResultPollingTimeout$ = new Subject<void>();

  emitQuizResultDataReady(): void {
    this.quizResultDataReady$.next();
  }

  emitQuizResultDataError(): void {
    this.quizResultDataError$.next();
  }

  emitQuizResultPollingTimeout(): void {
    this.quizResultPollingTimeout$.next();
  }

  onQuizResultDataReady$(): Observable<void> {
    return this.quizResultDataReady$.asObservable();
  }

  onQuizResultDataError$(): Observable<void> {
    return this.quizResultDataError$.asObservable();
  }

  onQuizResultPollingTimeout$(): Observable<void> {
    return this.quizResultPollingTimeout$.asObservable();
  }

  getShowQuizResultView() {
    return this.showQuizResultView();
  }
  setShowQuizResultView(show: boolean) {
    this.showQuizResultView.set(show);
  }

  // Certification Video Acknowledgement
  private acknowledgementSessionToken = signal<string | null>(null);
  private hideCameraToggle = signal<boolean>(false);
  private certAcknowledgementPending = signal<boolean>(false);

  private screenshareBlocked = signal<boolean>(false);

  getScreenshareBlocked(): boolean {
    return this.screenshareBlocked();
  }
  setScreenshareBlocked(blocked: boolean): void {
    this.screenshareBlocked.set(blocked);
  }

  getAcknowledgementSessionToken(): string | null {
    return this.acknowledgementSessionToken();
  }
  setAcknowledgementSessionToken(token: string | null) {
    this.acknowledgementSessionToken.set(token);
  }

  getHideCameraToggle(): boolean {
    return this.hideCameraToggle();
  }
  setHideCameraToggle(hide: boolean) {
    this.hideCameraToggle.set(hide);
  }

  getCertAcknowledgementPending() {
    return this.certAcknowledgementPending.asReadonly();
  }
  setCertAcknowledgementPending(pending: boolean) {
    this.certAcknowledgementPending.set(pending);
  }

  resetForNewCall(): void {
    // practiceCallUuid is safe to reset — re-set from startCall/startCallRequest API response
    this.practiceCallUuid.set('');

    // Call state
    this.currentView.set(null);
    this.call.set(null);
    this.isPaused.set(false);
    this.advanceType.set('MANUAL');
    this.displayPostCall.set(false);
    this.replaceWords.set([]);
    this.pronounceWords.set([]);
    this.callWords.set([]);
    this.sttWords.set([]);
    this.isCallComplete.set(false);
    this.quizResultReady.set(false);
    this.showQuizResultView.set(false);
    this.hints.set('');
    this.isResumeFromModal.set(false);
    this.resumeFromModalType.set(null);
    this.blockingModalOpenCount.set(0);
    this.displayAlert.set(false);
    this.isFullScreen.set(false);
    this.isVisualAidOpen.set(false);
    this.visualAidImageUrl.set('');
    this.visualAidImages.set([]);
    this.maxAttemptsAllowed.set(null);

    // Transcript
    this.lastMessageTimeStamp.set(new Date());
    this.recognizedTranscripts.set([]);
    this.speechTranscript.set('');
    this.speechTranscriptPartial.set('');
    this.partialTranscriptExists.set(false);
    this.transcriptChanges = new TranscriptCorrections();
    this.transcriptions.set([]);

    // Answer
    this.isSendingAnswer.set(false);
    this.isWaitingTimeout.set(false);
    this.answerData.set(null);
    this.wasSendingAnswer.set(false);

    // Avatar
    this.isAvatarSpeaking.set(false);
    this.isAvatarThinking.set(false);

    // Buttons
    this.isContinueButtonBlocked.set(false);
    this.isContinueButtonHidden.set(false);
    this.isMicOn.set(true);
    this.userMicPreference.set(true);
    this.disableEndCallButton.set(false);

    // User
    this.isAudioDeviceSelected.set(false);

    // Error handling
    this.isLoadingNoResponse.set(false);
    this.openNoResponseModal.set(false);
    this.isErrorOnAudioValidation.set(false);

    // Processing state
    this.isProcessingAnswer.set(false);
    this.isAssemblyInitialized.set(false);
    this.isReconnectingAudio.set(false);
    this.isAudioCallMode.set(false);

    // Quiz
    this.quizResultData.set(null);

    // Certification Video Acknowledgement
    this.certAcknowledgementPending.set(false);

    this.screenshareBlocked.set(false);

    // NOT reset: callUuid, callType, moduleAssignmentUuid, moduleAssignmentType, isPersonalizedCall
    //   Set by setQueryParams() in call.component.ts BEFORE startCall() is triggered.
    //   Never re-populated from the API response — wiping them would leave them empty for
    //   the entire call duration (used by AssemblyService.init, UI components, logger, etc).
    // NOT reset: personalizedLaunchPayload
    //   Set from personalized-practice page before navigation. Read in startCallRequest():1299
    //   to send payload to API. Wiping it would silently drop the personalized call payload.
    // NOT reset: acknowledgementSessionToken
    //   Set in checkCertificationAcknowledgement() before startCall(). Read directly in
    //   startCall():706 and startCallRequest():1308. Wiping it would lose the cert token.
    // NOT reset: hideCameraToggle
    //   Set to true for certification calls (call.component.ts:280) and must persist through
    //   the entire call. Wiping it would make camera toggle button reappear during cert calls.
    // NOT reset: skipConnectTranscriber
    //   call.component.ts:160 sets it to true in ngOnInit() BEFORE startCall() fires.
    //   resetRecognitionControls() also sets it to true at call end.
    //   Resetting to false here wipes the true value, causing onLoopInactive() to connect
    //   the transcriber immediately on the first loop instead of skipping it.
    // NOT reset: isIpad, language, userUuid — persist across calls by design
  }
}
